import KSelect from "./KSelect";
import KTextField from "./KTextField";
import { formatTime } from "kinnect-formatters";
import {
	VBtn,
	VCard,
	VCardActions,
	VCardText,
	VCardTitle,
	VCol,
	VContainer,
	VDialog,
	VMenu,
	VMessages,
	VRow,
	VSpacer
} from "vuetify/lib";

export const defaultMenuProps = {
	bottom: true,
	closeOnContentClick: false,
	fullWidth: true
};

export const defaultTextFieldProps = {
	prependIcon: "access_time"
};

export default {
	name: "KTimePicker",

	props: {
		disabled: Boolean,
		menuProps: {
			default: () => defaultMenuProps,
			type: Object
		},
		textFieldProps: {
			default: () => defaultTextFieldProps,
			type: Object
		},
		value: String
	},

	data: () => ( {
		hours: Array.from(
			Array( 12 ).keys(),
			( v, k ) => `${k + 1}`
		),
		initialValue: null,
		internalValue: null,
		minutes: Array.from(
			Array( 60 ).keys(),
			( v, k ) => ( k < 10 ? `0${k}` : `${k}` )
		),
		open: false,
		periods: [ "AM", "PM" ],
		width: "360px"
	} ),

	computed: {
		$_menuProps() {
			return {
				...defaultMenuProps,
				...this.menuProps
			};
		},

		$_textFieldProps() {
			return {
				...defaultTextFieldProps,
				...this.textFieldProps
			};
		},

		clearable() {
			return !this.textFieldProps.required && !this.disabled;
		},

		displayValue() {
			if ( this.value ) {
				return formatTime( this.value );
			}
		},

		dialogErrorMessages() {
			const messages = [];

			if ( /^00:/.test( this.internalValue ) ) {
				messages.push( "WARNING: This is midnight" );
			}

			return messages;
		},

		inputHour: {
			get() {
				if ( this.internalValue ) {
					return formatTime( this.internalValue, "h" );
				}
			},
			set( hour ) {
				this.updateInternalValue( hour, this.inputMinute, this.inputPeriod );
			}
		},

		inputMinute: {
			get() {
				if ( this.internalValue ) {
					return formatTime( this.internalValue, "mm" );
				}
			},
			set( minute ) {
				this.updateInternalValue( this.inputHour, minute, this.inputPeriod );
			}
		},

		inputPeriod: {
			get() {
				if ( this.internalValue ) {
					return formatTime( this.internalValue, "a" );
				}
			},
			set( period ) {
				this.updateInternalValue( this.inputHour, this.inputMinute, period );
			}
		}
	},

	watch: {
		open( open ) {
			if ( open ) {
				this.initialValue = this.value;
				this.internalValue = this.value;
			} else {
				this.internalValue = this.value;
			}
		}
	},

	methods: {
		emitInput( val ) {
			this.$emit( "input", val );
			this.internalValue = val;
		},

		updateInternalValue( hour, minute, period ) {
			this.internalValue = formatTime(
				`${hour || "00"}:${minute || "00"} ${period || "AM"}`,
				"HH:mm:ss"
			);
		},

		clear() {
			this.emitInput( null );
			this.open = false;
		},

		save() {
			this.emitInput( this.internalValue );
			this.open = false;
		},

		scrollList( e, list, propKey ) {
			const selectedIndex = list.indexOf( this[propKey] );

			if ( e.deltaY > 0 ) {
				if ( selectedIndex === -1 || selectedIndex === list.length - 1 ) {
					this[propKey] = list[0];
				} else {
					this[propKey] = list[selectedIndex + 1];
				}
			} else if ( selectedIndex === -1 || selectedIndex === 0 ) {
				this[propKey] = list[list.length - 1];
			} else {
				this[propKey] = list[selectedIndex - 1];
			}

			e.preventDefault();
		},

		hourScroll( e ) {
			this.scrollList( e, this.hours, "inputHour" );
		},

		minuteScroll( e ) {
			this.scrollList( e, this.minutes, "inputMinute" );
		},

		periodScroll( e ) {
			this.scrollList( e, this.periods, "inputPeriod" );
		},

		containerScroll( e ) {
			e.preventDefault();
		}
	},

	render() {
		const TagName = this.$vuetify.breakpoint.smAndDown ? VDialog : VMenu;

		return <TagName
			attrs={{
				...this.$_menuProps,
				disabled: this.disabled,
				maxWidth: this.width,
				minWidth: this.width
			}}
			scopedSlots={{
				activator: ( { on } ) =>
					<KTextField
						attrs={{
							...this.$_textFieldProps,
							clearable: this.clearable,
							disabled: this.disabled,
							readonly: true,
							validationMode: "aggressive",
							value: this.displayValue
						}}
						directives={this.$vnode.data.directives}
						on={{
							...on,
							"click:clear": () => this.emitInput( null )
						}}
					></KTextField>
			}}
			vModel={this.open}
		>
			<VCard nativeOnWheel={this.containerScroll}>
				<VCardTitle class="text-subtitle-1 primary white--text">
					{this.textFieldProps.label}
				</VCardTitle>

				<VCardText>
					<VContainer class="pa-0">
						<VRow>
							<VCol cols={6} sm={4}>
								<KSelect
									hideDetails
									items={this.hours}
									label="Hour"
									nativeOnWheel={this.hourScroll}
									vModel={this.inputHour}
								></KSelect>
							</VCol>

							<VCol cols={6} sm={4}>
								<KSelect
									hideDetails
									items={this.minutes}
									label="Minute"
									nativeOnWheel={this.minuteScroll}
									vModel={this.inputMinute}
								></KSelect>
							</VCol>

							<VCol cols={12} sm={4}>
								<KSelect
									hideDetails
									items={this.periods}
									label="AM / PM"
									nativeOnWheel={this.periodScroll}
									vModel={this.inputPeriod}
								></KSelect>
							</VCol>

							<VCol class="py-0" cols={12}>
								<VMessages
									color="error"
									value={this.dialogErrorMessages}
								></VMessages>
							</VCol>
						</VRow>
					</VContainer>
				</VCardText>

				<VCardActions>
					{this.clearable && <VBtn onClick={this.clear} text>Clear</VBtn>}

					<VSpacer></VSpacer>

					<VBtn
						color="link"
						onClick={this.save}
						text
					>
						Save
					</VBtn>
				</VCardActions>
			</VCard>
		</TagName>;
	}
};
